import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';

const quickActions = [
  { path: '/admin/social', icon: '📱', label: 'Social Media', sub: 'Schedule April posts' },
  { path: '/admin',        icon: '📅', label: 'Schedule',     sub: 'Update bowling hours' },
];

const upcomingEvents = [
  { date: 'Apr 5',  title: 'Saturday Open Bowling Night',    tag: 'Open',    tagColor: '#2AD5FF' },
  { date: 'Apr 9',  title: 'Spring League Kickoff Night',    tag: 'League',  tagColor: '#c39bd3' },
  { date: 'Apr 12', title: 'Kids Bowl Free Weekend',         tag: 'Family',  tagColor: '#27ae60' },
  { date: 'Apr 19', title: 'Glow Bowling Friday Night',      tag: 'Special', tagColor: '#e94560' },
  { date: 'Apr 26', title: 'Corporate Group Event',          tag: 'Private', tagColor: '#FFD200' },
];

const recentActivity = [
  { color: '#27ae60', text: 'New member joined — Maria S. registered as Gold member',     time: 'Today, 4:32 PM' },
  { color: '#2AD5FF', text: 'Lane 12 reserved — Birthday Party (Johnson Family)',          time: 'Today, 2:15 PM' },
  { color: '#FFD200', text: 'Facebook post scheduled — League Night #1 for Apr 2',        time: 'Today, 11:40 AM' },
  { color: '#563DE4', text: 'Spring League roster updated — 14 teams confirmed',           time: 'Yesterday, 6:10 PM' },
  { color: '#16a085', text: 'Bar menu updated — 3 new spring items added',                time: 'Yesterday, 3:48 PM' },
];

const socialPreview = [
  { color: '#c39bd3', text: '🎳 It\'s League Night at Skylanes! Join our bowlers every week...', date: 'Apr 2' },
  { color: '#76d7c4', text: '🌲 Heading to the Poconos this weekend? Roll a few frames...', date: 'Apr 4' },
  { color: '#f9ca6b', text: '🍔🍺 Game day is ALWAYS better at Skylanes!', date: 'Apr 5' },
  { color: '#f1948a', text: '❤️ Skylanes has been part of this community for years...', date: 'Apr 7' },
];

function AdminHome() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate('/admin/login');
      } else {
        setUser(currentUser);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/admin/login');
  };

  if (loading) return <div className="admin-loading">Loading...</div>;
  if (!user) return null;

  return (
    <div className="ah-layout">
      <AdminSidebar user={user} onLogout={handleLogout} />

      <main className="ah-main">
        {/* Top bar */}
        <div className="ah-topbar">
          <div>
            <h1 className="ah-page-title">Good evening, Admin 👋</h1>
            <p className="ah-page-sub">Skylanes Bowling Center — April 2026</p>
          </div>
          <Link to="/admin/social" className="ah-btn ah-btn-primary">📱 Manage Social Posts</Link>
        </div>

        {/* Stat cards */}
        <div className="ah-stats-grid">
          <div className="ah-stat-card" style={{ '--sc': '#2AD5FF' }}>
            <div className="ah-stat-icon">💵</div>
            <div className="ah-stat-val">$14.2k</div>
            <div className="ah-stat-lbl">Monthly Revenue</div>
            <div className="ah-stat-sub ah-up">▲ 12% vs last month</div>
          </div>
          <div className="ah-stat-card" style={{ '--sc': '#FFD200' }}>
            <div className="ah-stat-icon">👥</div>
            <div className="ah-stat-val">342</div>
            <div className="ah-stat-lbl">Active Members</div>
            <div className="ah-stat-sub ah-up">▲ 23 new this month</div>
          </div>
          <div className="ah-stat-card" style={{ '--sc': '#563DE4' }}>
            <div className="ah-stat-icon">🏆</div>
            <div className="ah-stat-val">6</div>
            <div className="ah-stat-lbl">Active Leagues</div>
            <div className="ah-stat-sub">2 currently forming</div>
          </div>
          <div className="ah-stat-card" style={{ '--sc': '#16a085' }}>
            <div className="ah-stat-icon">🎟️</div>
            <div className="ah-stat-val">4</div>
            <div className="ah-stat-lbl">Upcoming Events</div>
            <div className="ah-stat-sub">Next: Apr 5</div>
          </div>
          <div className="ah-stat-card" style={{ '--sc': '#e94560' }}>
            <div className="ah-stat-icon">📱</div>
            <div className="ah-stat-val">16</div>
            <div className="ah-stat-lbl">Posts This Month</div>
            <div className="ah-stat-sub">0 scheduled so far</div>
          </div>
        </div>

        {/* Content grid */}
        <div className="ah-content-grid">

          {/* Quick Actions */}
          <div className="ah-panel">
            <div className="ah-panel-header">
              <h3>⚡ Quick Actions</h3>
            </div>
            <div className="ah-panel-body">
              <div className="ah-qa-grid">
                {quickActions.map(action => (
                  <Link key={action.path} to={action.path} className="ah-qa-btn">
                    <span className="ah-qa-icon">{action.icon}</span>
                    <div>
                      <div className="ah-qa-label">{action.label}</div>
                      <div className="ah-qa-sub">{action.sub}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="ah-panel">
            <div className="ah-panel-header">
              <h3>📅 Upcoming Events</h3>
              <Link to="/admin/events" className="ah-btn ah-btn-sm ah-btn-outline">View all</Link>
            </div>
            <div className="ah-panel-body">
              {upcomingEvents.map((ev, i) => (
                <div className="ah-row" key={i}>
                  <span className="ah-date-pill">{ev.date}</span>
                  <span className="ah-row-title">{ev.title}</span>
                  <span className="ah-tag" style={{ color: ev.tagColor, background: `${ev.tagColor}22` }}>{ev.tag}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="ah-panel">
            <div className="ah-panel-header">
              <h3>🕐 Recent Activity</h3>
            </div>
            <div className="ah-panel-body">
              {recentActivity.map((act, i) => (
                <div className="ah-activity-row" key={i}>
                  <div className="ah-activity-dot" style={{ background: act.color }} />
                  <div>
                    <div className="ah-activity-text">{act.text}</div>
                    <div className="ah-activity-time">{act.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Social Media Preview */}
          <div className="ah-panel">
            <div className="ah-panel-header">
              <h3>📱 April Social Schedule</h3>
              <Link to="/admin/social" className="ah-btn ah-btn-sm ah-btn-outline">Manage</Link>
            </div>
            <div className="ah-panel-body">
              {socialPreview.map((p, i) => (
                <div className="ah-sm-row" key={i}>
                  <div className="ah-sm-dot" style={{ background: p.color }} />
                  <div className="ah-sm-text">{p.text}</div>
                  <div className="ah-sm-date">{p.date}</div>
                </div>
              ))}
              <div className="ah-sm-row" style={{ opacity: 0.5, fontStyle: 'italic' }}>
                <div className="ah-sm-dot" style={{ background: '#8a8a9a' }} />
                <div className="ah-sm-text">+ 12 more posts through April 29</div>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}

export default AdminHome;
